type Particle = {
  el: HTMLImageElement;
  ambient: boolean;
  x: number;
  y: number;
  vy: number;
  op: number;
  rot: number;
  vr: number;
  sa: number;
  sf: number;
  ph: number;
  ox: number;
  oy: number;
  g: number;
  age: number;
  life: number;
};

const rand = (a: number, b: number) => a + Math.random() * (b - a);
const ease = (t: number) => 1 - Math.pow(1 - t, 3);

export const createGatherField = (
  layer: HTMLElement,
  symbolSrc: string,
  count: number,
) => {
  const panel = layer.parentElement ?? layer;
  const mouse = {x: 0, y: 0, inside: false};
  let parts: Particle[] = [];
  let width = panel.offsetWidth || 560;
  let height = panel.offsetHeight || 800;

  const measure = () => {
    width = panel.offsetWidth || 560;
    height = panel.offsetHeight || 800;
  };

  const image = (size: number) => {
    const el = document.createElement('img');
    el.src = symbolSrc;
    el.alt = '';
    Object.assign(el.style, {
      position: 'absolute',
      left: '0',
      top: '0',
      width: `${size}px`,
      height: `${size}px`,
      marginLeft: `${-size / 2}px`,
      marginTop: `${-size / 2}px`,
      willChange: 'transform,opacity',
      opacity: '0',
    });
    layer.appendChild(el);
    return el;
  };

  const particle = (
    overrides: Partial<Omit<Particle, 'el'>>,
    size = rand(14, 30),
  ): Particle => ({
    el: image(size),
    ambient: true,
    x: rand(10, width - 10),
    y: rand(0, height),
    vy: -rand(14, 28),
    op: rand(0.22, 0.5),
    rot: rand(0, 90),
    vr: rand(-8, 8),
    sa: rand(4, 12),
    sf: rand(0.4, 0.9),
    ph: rand(0, 6.3),
    ox: 0,
    oy: 0,
    g: 0,
    age: 0,
    life: 0,
    ...overrides,
  });

  parts = Array.from({length: count}, () => particle({}));

  const celebrate = () => {
    for (let i = 0; i < 12; i++) {
      parts.push(
        particle(
          {
            ambient: false,
            x: rand(20, width - 20),
            y: height + rand(10, 80),
            vy: -rand(150, 230),
            op: rand(0.6, 0.9),
            vr: rand(-40, 40),
            sa: rand(6, 14),
            sf: rand(1, 2),
            age: -i * 0.06,
            life: 3.2,
          },
          rand(16, 32),
        ),
      );
    }
  };

  const tickAmbient = (p: Particle, dt: number) => {
    p.y += p.vy * dt;
    p.rot += p.vr * dt;
    if (p.y < -40) {
      p.y = height + 40;
      p.x = rand(10, width - 10);
    }
    let tx = 0;
    let ty = 0;
    let glow = 0;
    if (mouse.inside) {
      const dx = mouse.x - p.x;
      const dy = mouse.y - p.y;
      const pull = Math.max(0, 1 - Math.hypot(dx, dy) / 260);
      tx = dx * pull * 0.4;
      ty = dy * pull * 0.4;
      glow = pull * 0.5;
    }
    const f = Math.min(1, dt * 3);
    p.ox += (tx - p.ox) * f;
    p.oy += (ty - p.oy) * f;
    p.g += (glow - p.g) * f;
    const edge = Math.min(1, (height + 40 - p.y) / 120, (p.y + 40) / 120);
    const sway = Math.sin(p.ph + p.age * p.sf) * p.sa;
    p.el.style.transform = `translate3d(${p.x + sway + p.ox}px,${p.y + p.oy}px,0) rotate(${p.rot}deg) scale(${1 + p.g * 0.7})`;
    p.el.style.opacity = String(Math.max(0, Math.min(1, (p.op + p.g) * edge)));
  };

  const tickBurst = (p: Particle, dt: number) => {
    p.y += p.vy * dt;
    p.vy *= Math.exp(-0.6 * dt);
    p.rot += p.vr * dt;
    const t = Math.min(1, p.age / p.life);
    const fade =
      (t < 0.08 && t / 0.08) || (t > 0.55 && 1 - (t - 0.55) / 0.45) || 1;
    p.el.style.transform = `translate3d(${p.x + Math.sin(p.ph + p.age * p.sf) * p.sa}px,${p.y}px,0) rotate(${p.rot}deg) scale(${0.5 + 0.5 * ease(Math.min(1, p.age / 0.8))})`;
    p.el.style.opacity = String(Math.max(0, p.op * fade));
    return t >= 1;
  };

  const tick = (dt: number) => {
    for (let i = parts.length - 1; i >= 0; i--) {
      const p = parts[i];
      p.age += dt;
      if (p.ambient) {
        tickAmbient(p, dt);
      } else if (p.age >= 0 && tickBurst(p, dt)) {
        p.el.remove();
        parts.splice(i, 1);
      }
    }
  };

  const onMove = (e: PointerEvent) => {
    const rect = panel.getBoundingClientRect();
    const k = width / rect.width;
    mouse.x = (e.clientX - rect.left) * k;
    mouse.y = (e.clientY - rect.top) * k;
    mouse.inside = true;
  };
  const onLeave = () => {
    mouse.inside = false;
  };

  const observer = new ResizeObserver(measure);
  observer.observe(panel);
  panel.addEventListener('pointermove', onMove);
  panel.addEventListener('pointerleave', onLeave);

  let last = performance.now();
  let frame = requestAnimationFrame(function loop(now) {
    tick(Math.min(0.05, (now - last) / 1000));
    last = now;
    frame = requestAnimationFrame(loop);
  });

  const destroy = () => {
    cancelAnimationFrame(frame);
    observer.disconnect();
    panel.removeEventListener('pointermove', onMove);
    panel.removeEventListener('pointerleave', onLeave);
    parts.forEach(p => p.el.remove());
    parts = [];
  };

  return {celebrate, destroy};
};

export type GatherField = ReturnType<typeof createGatherField>;
